import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, ChevronLeft, ChevronRight, LucideIcon } from "lucide-react";
import {
  Heart,
  CreditCard,
  ShoppingCart,
  Store,
  Phone,
  Radio,
  Zap,
  Car,
  GraduationCap,
  Home,
  Factory,
  Truck,
  Clapperboard,
  Package,
  Power,
} from "lucide-react";

interface Industry {
  icon: LucideIcon;
  title: string;
  category: string;
  description: string;
  features: string[];
  technologies: string[];
  href: string;
}

interface IndustriesMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const IndustriesMenu = ({ isOpen, onClose }: IndustriesMenuProps) => {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const industryCategories: Record<string, Industry[]> = {
    Healthcare: [
      {
        icon: Heart,
        title: "Healthcare",
        category: "Healthcare",
        description:
          "Transforming healthcare through digital innovation with AI-driven healthcare platforms, telemedicine solutions, IoT-enabled diagnostics, and integrated care ecosystems for modern healthcare organizations.",
        features: [
          // Digital Health Platform Development
          "Custom Healthcare Platforms",
          "Patient Engagement Systems",
          "Health Data Management",
          "Healthcare Workflow Automation",

          // Telemedicine Solutions
          "Remote Consultation Systems",
          "Virtual Healthcare Delivery",
          "Doctor-Patient Communication",
          "Video Consultation Integration",

          // EMR / EHR Integration
          "Electronic Medical Records",
          "Healthcare Interoperability",
          "Data Synchronization",
          "Clinical Workflow Integration",

          // IoT & Point of Care Devices
          "Smart Healthcare Devices",
          "Sensor Integrations",
          "Remote Patient Monitoring",
          "Device Connectivity Ecosystem",

          // Healthcare Analytics
          "Population Health Analytics",
          "Clinical Insights",
          "Predictive Healthcare Analytics",
          "Operational Dashboards",

          // Healthcare Commerce Solutions
          "Online Pharmacy Systems",
          "Healthcare Marketplace",
          "Insurance Integrations",
          "SaaS Healthcare Ecosystem",
        ],
        technologies: [
          // Core Platform Features
          "AI-based Healthcare Solutions",
          "Telemedicine Integration",
          "EMR/EHR Management",
          "IoT Device Connectivity",
          "Health Wallet",
          "Population Health Management",
          "Appointment Scheduling",
          "Real-time Healthcare Analytics",
          "Patient Engagement Tools",
          "Secure Cloud Infrastructure",
          "Healthcare Compliance Framework",
          "Mobile Healthcare Applications",

          // Compliance + Interoperability
          "HIPAA Compliance",
          "HL7 FHIR",
          "DICOM",
          "EMR Integration",
          "EHR Systems",
          "Healthcare SaaS",
        ],
        href: "/industries/healthcare",
      },
    ],
    Finance: [
      {
        icon: CreditCard,
        title: "FinTech & BFSI",
        category: "Finance",
        description:
          "Build secure, scalable financial technology solutions that comply with regulatory requirements.",
        features: [
          "Digital Banking",
          "Payment Processing",
          "Blockchain Solutions",
          "Risk Management",
        ],
        technologies: [
          "PCI DSS",
          "SOX Compliance",
          "Blockchain",
          "Open Banking APIs",
          "Core Banking",
          "RegTech",
        ],
        href: "/industries/fintech",
      },
    ],
    // {
    //   icon: ShoppingCart,
    //   title: "E-commerce",
    //   category: "Retail",
    //   description:
    //     "Create compelling online shopping experiences with robust e-commerce platforms and solutions.",
    //   features: [
    //     "Multi-vendor Marketplaces",
    //     "Payment Gateways",
    //     "Inventory Management",
    //     "Customer Analytics",
    //   ],
    //   technologies: [
    //     "Shopify Plus",
    //     "WooCommerce",
    //     "Magento",
    //     "Stripe",
    //     "PayPal",
    //     "Amazon Web Services",
    //   ],
    //   href: "/industries/ecommerce",
    // },
    // {
    //   icon: Store,
    //   title: "Retail",
    //   category: "Retail",
    //   description:
    //     "Modernize retail operations with omnichannel solutions and customer engagement platforms.",
    //   features: [
    //     "POS Systems",
    //     "Omnichannel Experience",
    //     "Supply Chain Management",
    //     "Customer Loyalty Programs",
    //   ],
    //   technologies: [
    //     "Salesforce Commerce",
    //     "Oracle Retail",
    //     "SAP Retail",
    //     "Microsoft Dynamics",
    //     "Square",
    //   ],
    //   href: "/industries/retail",
    // },
    Commerce: [
      {
        icon: ShoppingCart,
        title: "E-commerce & Retail",
        category: "Digital Commerce & Retail",
        description:
          "Transform retail and commerce through smart digital solutions with intelligent e-commerce platforms, omnichannel retail systems, retail distribution ecosystems, procurement automation, and seamless customer experiences.",
        features: [
          "Digital Commerce Enablement",
          "Multi-vendor Marketplace",
          "B2B Commerce Platforms",
          "B2C E-commerce Solutions",
          "Hyperlocal Commerce",
          "Retail & Dealer Network Management",
          "Rural & Urban Retail Distribution",
          "Smart Procurement Systems",
          "Inventory & Warehouse Management",
          "Supply Chain Optimization",
          "Omnichannel Retail Operations",
          "Customer-Centric Shopping Experiences",
        ],
        technologies: [
          "Shopify Plus",
          "WooCommerce",
          "Magento",
          "Salesforce Commerce",
          "Oracle Retail",
          "SAP Retail",
          "Microsoft Dynamics",
          "Stripe",
          "PayPal",
          "Square",
          "AWS",
          "RFID",
          "IoT",
          "Analytics",
          "Mobile Commerce",
        ],
        href: "/industries/commerce",
      },
    ],
    Industrial: [
      {
        icon: Car,
        title: "Manufacturing & Automotive",
        category: "Industrial",
        description:
          "Empower automotive manufacturers, OEMs, Tier-1 suppliers, and mobility enterprises with next-generation digital engineering across connected vehicles, EV platforms, manufacturing automation, diagnostics, analytics, and enterprise transformation.",
        features: [
          "Connected Vehicle Platforms",
          "Real-time Vehicle Monitoring",
          "IoT-enabled Telematics Integration",
          "Fleet Management Systems",
          "Vehicle Diagnostics & Predictive Maintenance",
          "EV Software Platforms",
          "Battery Monitoring Systems",
          "Charging Infrastructure Management",
          "Embedded Automotive Software",
          "Crash Data & Vehicle Health Analytics",
          "Industry 4.0 Smart Factory Solutions",
          "Manufacturing Automation",
        ],
        technologies: [
          "IoT",
          "Telematics",
          "V2X Communication",
          "AUTOSAR",
          "CAN Bus",
          "OBD-II",
          "Embedded Systems",
          "AI/ML",
          "Cloud Platforms",
          "PLC/SCADA",
          "MES",
          // "OPC UA",
          "Kafka",
          "Edge Computing",
        ],
        href: "/industries/industrial",
      },
    ],
    // {
    //   icon: Phone,
    //   title: "Telecom",
    //   category: "Telecommunications",
    //   description:
    //     "Enhance telecommunications infrastructure with cutting-edge network and customer management solutions.",
    //   features: [
    //     "Network Management",
    //     "Billing Systems",
    //     "Customer Service Platforms",
    //     "IoT Integration",
    //   ],
    //   technologies: [
    //     "5G",
    //     "SDN/NFV",
    //     "OSS/BSS",
    //     "VoIP",
    //     "SIP",
    //     "Network Orchestration",
    //   ],
    //   href: "/industries/telecom",
    // },
    // {
    //   icon: Radio,
    //   title: "Media",
    //   category: "Media & Entertainment",
    //   description:
    //     "Deliver engaging digital media experiences with content management and distribution platforms.",
    //   features: [
    //     "Content Management",
    //     "Video Streaming",
    //     "Digital Publishing",
    //     "Audience Analytics",
    //   ],
    //   technologies: [
    //     "CDN",
    //     "Video Encoding",
    //     "DRM",
    //     "CMS",
    //     "Adobe Creative Suite",
    //     "AWS Media Services",
    //   ],
    //   href: "/industries/media",
    // },
    Energy: [
      {
        icon: Zap,
        title: "Energy & Utilities Solutions",
        category: "Energy & Infrastructure",
        description:
          "Modernize energy and utility ecosystems with smart grid platforms, renewable energy solutions, industrial IoT, predictive maintenance, and intelligent digital transformation.",
        features: [
          "Smart Grid Management Platforms",
          "Real-time Energy Monitoring",
          "Utility Asset Management",
          "Demand Forecasting Systems",
          "Remote Operations Management",
          "Solar Farm Monitoring",
          "Wind Turbine Analytics",
          "Renewable Asset Performance Tracking",
          // "SCADA & OPC UA Integration",
          "Industrial IoT Automation",
          "Predictive Maintenance",
          "Asset Lifecycle Management",
        ],
        technologies: [
          "SCADA",
          // "OPC UA",
          "PLC",
          "IoT Sensors",
          "Smart Meters",
          "AI/ML",
          "Grid Analytics",
          "GIS",
          "Edge Computing",
          "Industrial Automation",
          "Cloud Platforms",
        ],
        href: "/industries/energyUtilities",
      },
    ],
    // Temp Comment Out  IMPORTANT
    // Telecom: [
    //   {
    //     icon: Radio,
    //     title: "Telecom & Media",
    //     category: "Telecommunications & Entertainment",
    //     description:
    //       "Empower telecommunications and media enterprises with advanced network infrastructure, digital content platforms, streaming solutions, and customer engagement technologies.",
    //     features: [
    //       "Network Management Systems",
    //       "OSS/BSS Platforms",
    //       "Customer Service & Billing Systems",
    //       "IoT Integration",
    //       "Content Management Platforms",
    //       "Video Streaming Solutions",
    //       "Digital Publishing",
    //       "Audience Analytics",
    //       "Content Distribution & Delivery",
    //       "Media Monetization Platforms",
    //     ],
    //     technologies: [
    //       "5G",
    //       "SDN/NFV",
    //       "OSS/BSS",
    //       "VoIP",
    //       "SIP",
    //       "Network Orchestration",
    //       "CDN",
    //       "Video Encoding",
    //       "DRM",
    //       "CMS",
    //       "Adobe Creative Suite",
    //       "AWS Media Services",
    //     ],
    //     href: "/industries/telecomMedia",
    //   },
    // ],

    // {
    //   icon: Zap,
    //   title: "Energy",
    //   category: "Energy & Utilities",
    //   description:
    //     "Optimize energy operations with smart grid solutions and renewable energy management systems.",
    //   features: [
    //     "Smart Grid Management",
    //     "Energy Trading",
    //     "Asset Management",
    //     "Regulatory Compliance",
    //   ],
    //   technologies: [
    //     "SCADA",
    //     "IoT Sensors",
    //     "Blockchain",
    //     "AI/ML",
    //     "GIS",
    //     "Meter Data Management",
    //   ],
    //   href: "/industries/energy",
    // },
    // {
    //   icon: Car,
    //   title: "Automotive",
    //   category: "Automotive",
    //   description:
    //     "Drive innovation in automotive industry with connected vehicle solutions and manufacturing systems.",
    //   features: [
    //     "Connected Vehicles",
    //     "Fleet Management",
    //     "Manufacturing Execution",
    //     "Dealer Management",
    //   ],
    //   technologies: [
    //     "IoT",
    //     "V2X Communication",
    //     "AUTOSAR",
    //     "CAN Bus",
    //     "OBD-II",
    //     "Telematics",
    //   ],
    //   href: "/industries/automotive",
    // },
    Education: [
      {
        icon: GraduationCap,
        title: "Education",
        category: "Education",
        description:
          "Transform learning experiences with digital education platforms and student management systems.",
        features: [
          "Learning Management Systems",
          "Student Information Systems",
          "Online Assessment",
          "Virtual Classrooms",
        ],
        technologies: [
          "Moodle",
          "Canvas",
          "Blackboard",
          "Zoom",
          "Microsoft Teams",
          "Google Workspace",
        ],
        href: "/industries/education",
      },
    ],
    // Temp Comment Out  IMPORTANT
    // RealEstate: [
    //   {
    //     icon: Home,
    //     title: "Real Estate",
    //     category: "Real Estate",
    //     description:
    //       "Revolutionize property management and real estate transactions with innovative digital solutions.",
    //     features: [
    //       "Property Management",
    //       "CRM Systems",
    //       "Virtual Tours",
    //       "Market Analytics",
    //     ],
    //     technologies: [
    //       "MLS Integration",
    //       "Zillow API",
    //       "Property Management Software",
    //       "VR/AR",
    //       "Blockchain",
    //     ],
    //     href: "/industries/real-estate",
    //   },
    // ],

    // {
    //   icon: Factory,
    //   title: "Manufacturing",
    //   category: "Manufacturing",
    //   description:
    //     "Optimize manufacturing processes with Industry 4.0 solutions and smart factory technologies.",
    //   features: [
    //     "Production Planning",
    //     "Quality Management",
    //     "Supply Chain Optimization",
    //     "Predictive Maintenance",
    //   ],
    //   technologies: ["MES", "ERP", "IoT", "Digital Twin", "AI/ML", "Robotics"],
    //   href: "/industries/manufacturing",
    // },
    Logistics: [
      {
        icon: Truck,
        title: "Logistics",
        category: "Logistics & Transportation",
        description:
          "Transform logistics through smart transportation, last-mile delivery, warehousing, EV-powered mobility, and AI-driven digital logistics platforms for modern enterprise distribution.",
        features: [
          // Transportation Services
          "Dedicated Fleet Services",
          "Scheduled Transportation",
          "Route Optimization",
          "Multi-city Operations",
          "Fast Urban Delivery",
          "Local Transportation Solutions",
          "Business Distribution Support",
          "Time-sensitive Deliveries",
          "Regional Logistics Support",
          "Long-distance Goods Movement",
          "Enterprise Transportation Management",

          // Last Mile Delivery
          "Same-day Delivery",
          "Real-time Tracking",
          "Delivery Proof Systems",
          "Fast Dispatch Management",
          "Mobile Delivery Applications",

          // Warehousing
          "Inventory Management",
          "Order Fulfillment",
          "Distribution Management",
          "Storage Solutions",
          "Dispatch Operations",
          "Packaging Support",

          // EV Logistics
          "Electric Delivery Vehicles",
          "Reduced Carbon Emissions",
          "Energy-efficient Transportation",
          "Green Supply Chain Initiatives",
        ],
        technologies: [
          "GPS Fleet Tracking",
          "AI-based Route Planning",
          "Logistics Analytics Dashboard",
          "Real-time Shipment Visibility",
          "Automated Dispatch Systems",
          "Mobile Fleet Applications",
          "Delivery Monitoring",
          "Digital Workflow Automation",
          "Predictive Logistics Analytics",
          "Fleet Performance Tracking",
          "Barcode & Tracking Systems",
          "WMS",
          "TMS",
          "RFID",
          "IoT",
          "EV Fleet Systems",
        ],
        href: "/industries/logistics",
      },
    ],
    Entertainment: [
      {
        icon: Clapperboard,
        title: "Entertainment",
        category: "Entertainment",
        description:
          "Create immersive entertainment experiences with digital platforms and content delivery solutions.",
        features: [
          "Content Distribution",
          "User Engagement",
          "Monetization",
          "Rights Management",
        ],
        technologies: [
          "Streaming Platforms",
          "DRM",
          "CDN",
          "Video Analytics",
          "Social Integration",
        ],
        href: "/industries/entertainment",
      },
    ],
    // Temp Comment Out  IMPORTANT
    // Consumer: [
    //   {
    //     icon: Package,
    //     title: "Consumer Goods and Services",
    //     category: "Consumer Goods",
    //     description:
    //       "Transform consumer product businesses with digital solutions for brand engagement and service delivery.",
    //     features: [
    //       "Product Lifecycle Management",
    //       "Brand Management",
    //       "Customer Experience",
    //       "Service Optimization",
    //     ],
    //     technologies: [
    //       "PLM Systems",
    //       "CRM",
    //       "E-commerce Platforms",
    //       "Supply Chain",
    //       "Analytics",
    //       "Mobile Apps",
    //     ],
    //     href: "/industries/consumer-goods",
    //   },
    // ],

    // {
    //   icon: Power,
    //   title: "Utilities",
    //   category: "Utilities",
    //   description:
    //     "Modernize utility operations with smart infrastructure, billing systems, and customer service platforms.",
    //   features: [
    //     "Smart Grid Management",
    //     "Utility Billing",
    //     "Customer Information Systems",
    //     "Asset Management",
    //   ],
    //   technologies: [
    //     "Smart Meters",
    //     "SCADA Systems",
    //     "GIS",
    //     "CIS",
    //     "Meter Data Management",
    //     "Grid Analytics",
    //   ],
    //   href: "/industries/utilities",
    // },
  };

  const handleIndustryClick = (industry: Industry) => {
    navigate(industry.href);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="fixed top-14 left-0 right-0 bg-background border-b border-border shadow-xl z-[56]"
      onMouseEnter={() => {}}
      onMouseLeave={onClose}
    >
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">
          Industries We Serve
        </h3>
        {/* MOBILE LAYOUT */}
        <div className="block md:hidden">
          <div className="grid grid-cols-1 gap-4 max-h-[60vh] overflow-y-auto">
            {Object.entries(industryCategories).map(
              ([categoryName, industries], categoryIndex) => (
                <div key={categoryName}>
                  {/* Industries */}
                  <div className="space-y-2">
                    {industries.map((industry, industryIndex) => {
                      const Icon = industry.icon;

                      // Global alternating color logic while preserving mobile layout
                      const isPrimary =
                        (categoryIndex + industryIndex) % 2 === 0;

                      return (
                        <button
                          key={industryIndex}
                          onClick={() => handleIndustryClick(industry)}
                          className={`w-full group text-left px-3 py-3 text-sm text-foreground ${
                            isPrimary
                              ? "hover:text-primary hover:bg-primary/5 hover:border-primary/20"
                              : "hover:text-secondary hover:bg-secondary/5 hover:border-secondary/20"
                          } transition-all duration-200 rounded-md border border-transparent flex items-center gap-3`}
                        >
                          <div
                            className={`flex h-8 w-8 items-center justify-center rounded-md ${
                              isPrimary ? "bg-primary/10" : "bg-secondary/10"
                            } flex-shrink-0`}
                          >
                            <Icon
                              className={`h-4 w-4 ${
                                isPrimary
                                  ? "text-primary shadow-glow"
                                  : "text-secondary"
                              }`}
                            />
                          </div>

                          <div
                            className={`font-medium text-sm leading-tight ${
                              isPrimary
                                ? "group-hover:text-primary"
                                : "group-hover:text-secondary"
                            } transition-colors`}
                          >
                            {industry.title}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ),
            )}
          </div>
        </div>

        {/* DESKTOP LAYOUT */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(industryCategories).map(
            ([categoryName, industries], categoryIndex) => (
              <div key={categoryName} className="h-full flex flex-col">
                {/* Industries */}
                <div className="space-y-2 flex-1">
                  {industries.map((industry, industryIndex) => {
                    const Icon = industry.icon;

                    // Global alternating color logic while preserving layout
                    const isPrimary = (categoryIndex + industryIndex) % 2 === 0;

                    return (
                      <button
                        key={industryIndex}
                        onClick={() => handleIndustryClick(industry)}
                        className={`w-full group text-left px-3 py-3 text-sm text-foreground ${
                          isPrimary
                            ? "hover:text-primary hover:bg-primary/5 hover:border-primary/20"
                            : "hover:text-secondary hover:bg-secondary/5 hover:border-secondary/20"
                        } transition-all duration-200 rounded-md border border-transparent flex items-center gap-3`}
                      >
                        <div
                          className={`flex h-8 w-8 items-center justify-center rounded-md flex-shrink-0`}
                        >
                          <Icon
                            className={`h-4 w-4 ${
                              isPrimary
                                ? "text-primary shadow-glow"
                                : "text-secondary"
                            }`}
                          />
                        </div>

                        <div
                          className={`font-medium text-sm leading-tight ${
                            isPrimary
                              ? "group-hover:text-primary"
                              : "group-hover:text-secondary"
                          } transition-colors`}
                        >
                          {industry.title}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
};

export default IndustriesMenu;
